import React, { useState, useEffect } from 'react';
import { 
  Briefcase, UserCheck, Clock, AlertCircle, CheckCircle, FileText,
  Users, Building2, TrendingUp, Calendar, Filter, Search, RefreshCw,
  Download, Eye, Edit, Send, XCircle, BarChart3, PieChart, Activity,
  Award, Target, Mail, Phone, MapPin, DollarSign, ChevronRight
} from 'lucide-react';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Onboarding status options
const ONBOARDING_STATUSES = {
  PENDING: 'pending_hr_entry',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  DELAYED: 'delayed',
  AT_RISK: 'at_risk'
};

// Aging buckets
const AGING_BUCKETS = [
  { key: '0-7', label: '0-7 days', min: 0, max: 7, color: 'bg-green-100 text-green-800' },
  { key: '8-14', label: '8-14 days', min: 8, max: 14, color: 'bg-yellow-100 text-yellow-800' },
  { key: '15-30', label: '15-30 days', min: 15, max: 30, color: 'bg-orange-100 text-orange-800' },
  { key: '30+', label: '30+ days', min: 31, max: 999, color: 'bg-red-100 text-red-800' }
];

const HRHankDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Data states
  const [submissions, setSubmissions] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [recruiters, setRecruiters] = useState([]);

  // Filter states
  const [filters, setFilters] = useState({
    client: 'all',
    recruiter: 'all',
    salesManager: 'all',
    onboardingStatus: 'all',
    agingBucket: 'all',
    searchTerm: ''
  });

  // Metrics
  const [hrMetrics, setHrMetrics] = useState({
    totalAcceptedOffers: 0,
    pendingOnboarding: 0,
    avgTimeToOnboard: 0,
    completedOnboarding: 0,
    atRiskCandidates: 0,
    aging0to7: 0,
    aging8to14: 0,
    aging15to30: 0,
    aging30plus: 0
  });

  const [candidatesByClient, setCandidatesByClient] = useState([]);
  const [acceptedCandidates, setAcceptedCandidates] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    calculateMetrics();
  }, [submissions, candidates, jobs]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchSubmissions(),
        fetchCandidates(),
        fetchJobs(),
        fetchClients(),
        fetchRecruiters()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/submissions`);
      if (response.ok) {
        const data = await response.json();
        setSubmissions(data.submissions || []);
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  const fetchCandidates = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/candidates`);
      if (response.ok) {
        const data = await response.json();
        // API returns array directly
        const candidates = Array.isArray(data) ? data : (data.candidates || []);
        setCandidates(candidates);
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/jobs`);
      if (response.ok) {
        const data = await response.json();
        // API returns array directly
        const jobs = Array.isArray(data) ? data : (data.jobs || []);
        setJobs(jobs);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/customers`);
      if (response.ok) {
        const data = await response.json();
        setClients(data.customers || []);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchRecruiters = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users`);
      if (response.ok) {
        const data = await response.json();
        const recruiterUsers = (data.users || []).filter(u => 
          u.role === 'recruiter' || u.role === 'lead'
        );
        setRecruiters(recruiterUsers);
      }
    } catch (error) {
      console.error('Error fetching recruiters:', error);
    }
  };

  const calculateMetrics = () => {
    // Filter for accepted offers
    const acceptedOffers = submissions.filter(s => s.current_status === 'offer_accepted');
    
    // Calculate days since offer accepted for aging
    const today = new Date();
    const candidatesWithAging = acceptedOffers.map(sub => {
      const candidate = candidates.find(c => c.id === sub.candidate_id);
      const job = jobs.find(j => j.id === sub.job_id);
      
      const offerAcceptedDate = sub.updated_at ? new Date(sub.updated_at) : new Date(sub.submission_date);
      const daysSinceAccepted = Math.floor((today - offerAcceptedDate) / (1000 * 60 * 60 * 24));
      
      // Determine aging bucket
      let agingBucket = AGING_BUCKETS.find(b => 
        daysSinceAccepted >= b.min && daysSinceAccepted <= b.max
      ) || AGING_BUCKETS[AGING_BUCKETS.length - 1];

      // Mock onboarding status (would come from actual onboarding system)
      const onboardingStatus = daysSinceAccepted > 30 ? ONBOARDING_STATUSES.DELAYED :
                               daysSinceAccepted > 14 ? ONBOARDING_STATUSES.AT_RISK :
                               daysSinceAccepted > 7 ? ONBOARDING_STATUSES.IN_PROGRESS :
                               ONBOARDING_STATUSES.PENDING;

      return {
        ...sub,
        candidate,
        job,
        daysSinceAccepted,
        agingBucket,
        onboardingStatus,
        offerAcceptedDate
      };
    });

    setAcceptedCandidates(candidatesWithAging);

    // Calculate aging buckets
    const aging0to7 = candidatesWithAging.filter(c => c.agingBucket.key === '0-7').length;
    const aging8to14 = candidatesWithAging.filter(c => c.agingBucket.key === '8-14').length;
    const aging15to30 = candidatesWithAging.filter(c => c.agingBucket.key === '15-30').length;
    const aging30plus = candidatesWithAging.filter(c => c.agingBucket.key === '30+').length;

    // At-risk candidates (15+ days or specific status)
    const atRisk = candidatesWithAging.filter(c => 
      c.daysSinceAccepted >= 15 || c.onboardingStatus === ONBOARDING_STATUSES.AT_RISK
    ).length;

    // Pending onboarding (not completed)
    const pending = candidatesWithAging.filter(c => 
      c.onboardingStatus !== ONBOARDING_STATUSES.COMPLETED
    ).length;

    // Average time to onboard (mock - would need actual onboarding completion dates)
    const avgTimeToOnboard = candidatesWithAging.length > 0
      ? Math.round(candidatesWithAging.reduce((acc, c) => acc + c.daysSinceAccepted, 0) / candidatesWithAging.length)
      : 0;

    setHrMetrics({
      totalAcceptedOffers: acceptedOffers.length,
      pendingOnboarding: pending,
      avgTimeToOnboard,
      completedOnboarding: 0, // Would need actual completion data
      atRiskCandidates: atRisk,
      aging0to7,
      aging8to14,
      aging15to30,
      aging30plus
    });

    // Group by client
    const byClient = {};
    candidatesWithAging.forEach(c => {
      const clientName = c.client_name || 'Unknown';
      if (!byClient[clientName]) {
        byClient[clientName] = {
          name: clientName,
          count: 0,
          pending: 0,
          completed: 0
        };
      }
      byClient[clientName].count++;
      if (c.onboardingStatus !== ONBOARDING_STATUSES.COMPLETED) {
        byClient[clientName].pending++;
      } else {
        byClient[clientName].completed++;
      }
    });

    setCandidatesByClient(Object.values(byClient).sort((a, b) => b.count - a.count));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      [ONBOARDING_STATUSES.PENDING]: 'bg-blue-100 text-blue-800',
      [ONBOARDING_STATUSES.IN_PROGRESS]: 'bg-yellow-100 text-yellow-800',
      [ONBOARDING_STATUSES.COMPLETED]: 'bg-green-100 text-green-800',
      [ONBOARDING_STATUSES.DELAYED]: 'bg-red-100 text-red-800',
      [ONBOARDING_STATUSES.AT_RISK]: 'bg-orange-100 text-orange-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      [ONBOARDING_STATUSES.PENDING]: 'Pending HR Entry',
      [ONBOARDING_STATUSES.IN_PROGRESS]: 'In Progress',
      [ONBOARDING_STATUSES.COMPLETED]: 'Completed',
      [ONBOARDING_STATUSES.DELAYED]: 'Delayed',
      [ONBOARDING_STATUSES.AT_RISK]: 'At Risk'
    };
    return labels[status] || status;
  };

  // Overview Tab
  const OverviewTab = () => (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard
          title="Accepted Offers"
          value={hrMetrics.totalAcceptedOffers}
          icon={Award}
          color="blue"
          subtitle="pending onboarding"
        />
        <MetricCard
          title="Pending Onboarding"
          value={hrMetrics.pendingOnboarding}
          icon={Clock}
          color="orange"
          subtitle="not yet completed"
        />
        <MetricCard
          title="Avg Time to Onboard"
          value={`${hrMetrics.avgTimeToOnboard}d`}
          icon={Target}
          color="teal"
          subtitle="days"
        />
        <MetricCard
          title="Completed"
          value={hrMetrics.completedOnboarding}
          icon={CheckCircle}
          color="green"
          subtitle="fully onboarded"
        />
        <MetricCard
          title="At Risk"
          value={hrMetrics.atRiskCandidates}
          icon={AlertCircle}
          color="red"
          subtitle="needs attention"
        />
      </div>

      {/* Aging Buckets */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-blue-600" />
          Aging Analysis (Days Since Offer Accepted)
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {AGING_BUCKETS.map(bucket => {
            const count = hrMetrics[`aging${bucket.key.replace('-', 'to').replace('+', 'plus')}`] || 0;
            return (
              <div key={bucket.key} className="text-center">
                <div className={`${bucket.color} rounded-lg p-6 mb-2`}>
                  <p className="text-3xl font-bold">{count}</p>
                </div>
                <p className="text-sm font-medium text-gray-600">{bucket.label}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Candidates by Client */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Building2 className="h-5 w-5 text-blue-600" />
          Candidates by Client
        </h3>
        <div className="space-y-3">
          {candidatesByClient.slice(0, 10).map(client => (
            <div key={client.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-blue-600 rounded-lg flex items-center justify-center text-white font-semibold">
                  {client.name.charAt(0)}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{client.name}</p>
                  <p className="text-sm text-gray-500">{client.count} total candidates</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-sm font-semibold text-orange-600">{client.pending}</p>
                  <p className="text-xs text-gray-500">Pending</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-green-600">{client.completed}</p>
                  <p className="text-xs text-gray-500">Completed</p>
                </div>
              </div>
            </div>
          ))}
          {candidatesByClient.length === 0 && (
            <p className="text-center text-gray-500 py-4">No candidates with accepted offers</p>
          )}
        </div>
      </div>
    </div>
  );

  // Candidates Tab - Memoized to prevent input focus loss
  const CandidatesTab = React.useMemo(() => {
    const filteredCandidates = acceptedCandidates.filter(cand => {
      if (filters.client !== 'all' && cand.client_name !== filters.client) return false;
      if (filters.recruiter !== 'all' && cand.recruiter_id !== filters.recruiter) return false;
      if (filters.onboardingStatus !== 'all' && cand.onboardingStatus !== filters.onboardingStatus) return false;
      if (filters.agingBucket !== 'all' && cand.agingBucket.key !== filters.agingBucket) return false;
      if (filters.searchTerm) {
        const search = filters.searchTerm.toLowerCase();
        return cand.candidate_name?.toLowerCase().includes(search) ||
               cand.job_title?.toLowerCase().includes(search) ||
               cand.client_name?.toLowerCase().includes(search);
      }
      return true;
    });

    return (
      <div className="space-y-4">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={filters.searchTerm}
                onChange={(e) => setFilters({...filters, searchTerm: e.target.value})}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={filters.client}
              onChange={(e) => setFilters({...filters, client: e.target.value})}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Clients</option>
              {clients.map(c => (
                <option key={c.id} value={c.company_name || c.client_name}>
                  {c.company_name || c.client_name}
                </option>
              ))}
            </select>
            <select
              value={filters.recruiter}
              onChange={(e) => setFilters({...filters, recruiter: e.target.value})}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Recruiters</option>
              {recruiters.map(r => (
                <option key={r.id} value={r.id}>{r.full_name || r.username}</option>
              ))}
            </select>
            <select
              value={filters.onboardingStatus}
              onChange={(e) => setFilters({...filters, onboardingStatus: e.target.value})}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Statuses</option>
              {Object.values(ONBOARDING_STATUSES).map(status => (
                <option key={status} value={status}>{getStatusLabel(status)}</option>
              ))}
            </select>
            <select
              value={filters.agingBucket}
              onChange={(e) => setFilters({...filters, agingBucket: e.target.value})}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Aging</option>
              {AGING_BUCKETS.map(bucket => (
                <option key={bucket.key} value={bucket.key}>{bucket.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Candidates Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Candidate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recruiter</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job Req</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Offer Accepted</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days Pending</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Onboarding Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredCandidates.map(cand => (
                <tr key={cand.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                        {cand.candidate_name?.charAt(0) || 'C'}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{cand.candidate_name}</p>
                        <p className="text-xs text-gray-500">{cand.candidate_email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">{cand.client_name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{cand.recruiter_name}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{cand.job_title}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{formatDate(cand.offerAcceptedDate)}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${cand.agingBucket.color}`}>
                      {cand.daysSinceAccepted} days
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(cand.onboardingStatus)}`}>
                      {getStatusLabel(cand.onboardingStatus)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => setSelectedCandidate(cand)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {filteredCandidates.length === 0 && (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                    No candidates found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }, [acceptedCandidates, clients, recruiters, filters, formatDate, getStatusColor, getStatusLabel, setSelectedCandidate]);

  // Reports Tab
  const ReportsTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Report Cards */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            Accepted Offers by Client
          </h3>
          <p className="text-sm text-gray-600 mb-4">View all accepted offers grouped by client</p>
          <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2">
            <Download className="h-4 w-4" />
            Export Report
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-blue-600" />
            Offer-to-Onboard Duration
          </h3>
          <p className="text-sm text-gray-600 mb-4">Average time from offer acceptance to onboarding</p>
          <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2">
            <Download className="h-4 w-4" />
            Export Report
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            Recruiter Productivity
          </h3>
          <p className="text-sm text-gray-600 mb-4">Offers accepted and onboarded by recruiter</p>
          <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2">
            <Download className="h-4 w-4" />
            Export Report
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-600" />
            Candidate Aging Analysis
          </h3>
          <p className="text-sm text-gray-600 mb-4">Candidates by days since offer acceptance</p>
          <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2">
            <Download className="h-4 w-4" />
            Export Report
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <div className="h-12 w-12 bg-blue-600 rounded-full flex items-center justify-center text-white">
              <Briefcase className="h-6 w-6" />
            </div>
            HR Hank Dashboard
          </h1>
          <p className="text-gray-600 mt-1">Manage post-offer onboarding and coordination</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            <Filter className="h-4 w-4" />
            Filters
          </button>
          <button
            onClick={fetchAllData}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {[
              { key: 'overview', label: 'Overview', icon: Activity },
              { key: 'candidates', label: 'Candidates Onboarding', icon: UserCheck },
              { key: 'reports', label: 'Reports & Analytics', icon: BarChart3 }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.key
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
            </div>
          ) : (
            <>
              {activeTab === 'overview' && <OverviewTab />}
              {activeTab === 'candidates' && CandidatesTab}
              {activeTab === 'reports' && <ReportsTab />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, icon: Icon, color, subtitle }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    orange: 'from-orange-500 to-orange-600',
    teal: 'from-teal-500 to-teal-600',
    green: 'from-green-500 to-green-600',
    red: 'from-red-500 to-red-600'
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} rounded-lg p-6 text-white`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-white text-opacity-90 text-sm font-medium">{title}</p>
        <Icon className="h-8 w-8 text-white text-opacity-80" />
      </div>
      <p className="text-3xl font-bold">{value}</p>
      {subtitle && <p className="text-white text-opacity-75 text-xs mt-1">{subtitle}</p>}
    </div>
  );
};

export default HRHankDashboard;
